import { PublicPresentationStep } from '@modules/public/PresentationPage/types';
import {
  CopyContentBuilderRequest,
  PresentationContentBuilderItemResponse,
  PresentationEntryCompactResponse,
  PresentationId,
  PresentationResponse,
  PresentationStep,
  PresentationStepResponse,
  PublicPresentationDetailResponse,
  PublicPresentationResponse,
} from '@business/entities/presentation';
import { Endpoint } from '../endpoint';
import { PresentationSettingsFormData } from '@modules/presentation/PresentationPage/PresentationSettingsModal/PresentationSettingsModal';
import { PresentationBuilderFormData } from '@modules/presentation/PresentationPage/PresentationBuilder/PresentationBuilderPage';
import { PresentationEntryFormData } from '@modules/form/AddPresentationModal/AddPresentationModal';
import { ProposalResponse, ProposalTemplateId } from '@business/entities/proposal';
import { OrgId } from '@business/entities/organization';
import { StartPresentationFormData } from '@modules/presentation/StartPresentationModal/StartPresentationModal';

export function fetchOptions(id?: PresentationId) {
  return Endpoint.get<PresentationStep[]>(`@gc/presentation/options/${id || 0}`);
}

export function fetchPresentations() {
  return Endpoint.get<PresentationResponse[]>(`@gc/presentation`);
}

export function fetchPresentation(id: PresentationId) {
  return Endpoint.get<PresentationResponse>(`@gc/presentation/presentation/${id}`);
}

export function createPresentation(data: Partial<PresentationResponse>) {
  return Endpoint.post<PresentationResponse>(`@gc/presentation/create`, { data });
}

export function updatePresentation(id: PresentationId, data: Partial<PresentationResponse>) {
  return Endpoint.post<PresentationResponse>(`@gc/presentation/${id}`, { data });
}

export function duplicatePresentation(id: PresentationId, data: Partial<PresentationResponse>) {
  return Endpoint.post<PresentationResponse>(`@gc/presentation/duplicate/${id}`, { data });
}

export function updateSettings(id: PresentationId, data: Partial<PresentationSettingsFormData>) {
  return Endpoint.post<PresentationResponse>(`@gc/presentation/settings/${id}`, { data });
}

export function deletePresentation(presentationId: PresentationId) {
  return Endpoint.delete<void>(`@gc/presentation/${presentationId}`);
}

export function fetchPresentationStep(id: PresentationId, stepId: string) {
  return Endpoint.get<PresentationStepResponse>(`@gc/presentation/presentation-step/${id}/${stepId}`);
}

export function updateBuilderFormData(id: PresentationId, stepId: string, data: string) {
  return Endpoint.post<PresentationStepResponse>(`@gc/presentation/update-builder-form/${id}/${stepId}`, { data: { data } });
}

/* Public Routes */

export function fetchPublicPresentation(id: PresentationId, isTemplatePreviewPage = true, orgId?: OrgId, isPublic = false) {
  return Endpoint.get<PublicPresentationResponse>(`@gc/${isPublic ? 'public/' : ''}presentation/public-presentation/${id}`, {
    params: { orgId, isTemplatePreviewPage },
  });
}

export function fetchPublicDetailPresentation(id: PresentationId, isTemplatePreviewPage = true, orgId: OrgId) {
  return Endpoint.get<PublicPresentationDetailResponse>(`@gc/presentation/public-detail-presentation/${id}`, {
    params: { isTemplatePreviewPage, orgId },
  });
}

export function addRecommendedLineItem(presentationId: PresentationId, lineItemId: string, type: string, orgId?: string) {
  return Endpoint.post<PublicPresentationResponse>(`@gc/presentation/add-recommended-item/${presentationId}`, { data: { lineItemId, type, orgId } });
}

export function createPresentationEntry(orgId: string, folderId: string, data: PresentationEntryFormData) {
  return Endpoint.post<PublicPresentationResponse>(`@gc/presentation/create-entry/${data.presentationId}`, { data: { orgId, folderId, ...data } });
}

export function onNextStep(
  id: PresentationId,
  stepId: string,
  data: Record<string, string>,
  isTemplatePreviewPage = true,
  orgId?: OrgId,
  isPublic = false,
  formRawData?: any,
) {
  return Endpoint.post<{ step: PublicPresentationStep; presentation: PublicPresentationResponse }>(
    `@gc/${isPublic ? 'public/' : ''}presentation/next-step/${id}`,
    {
      data: { orgId, stepId, data, isTemplatePreviewPage, formRawData },
    },
  );
}

export function onBackStep(id: PresentationId, stepId: string, isTemplatePreviewPage = true, orgId?: OrgId, isPublic = false) {
  return Endpoint.post<{ step: PublicPresentationStep; presentation: PublicPresentationResponse }>(
    `@gc/${isPublic ? 'public/' : ''}presentation/back-step/${id}`,
    {
      data: { orgId, stepId, isTemplatePreviewPage },
    },
  );
}

export function createProposal(presentationId: PresentationId, templateId: ProposalTemplateId, isPublic = false, isPreviewPage = false) {
  return Endpoint.post<ProposalResponse>(`@gc/${isPublic ? 'public/' : ''}presentation/create-proposal/${presentationId}`, {
    data: { templateId, isPreviewPage },
  });
}

export function fetchContentBuilders(presentationId: PresentationId) {
  return Endpoint.get<PresentationContentBuilderItemResponse[]>(`@gc/presentation/content-builders`, { params: { presentationId } });
}

export function updateContentPage(id: PresentationId, data: CopyContentBuilderRequest) {
  return Endpoint.post<PresentationResponse>(`@gc/presentation/update-content-page/${id}`, { data });
}

export function startPresentation(data: StartPresentationFormData, isPublic = false) {
  return Endpoint.post<PresentationEntryCompactResponse>(`@gc/${isPublic ? 'public/' : ''}presentation/start-presentation`, { data });
}

export function fetchPresentationStepNames(id: PresentationId) {
  return Endpoint.get<Record<string, string>>(`@gc/presentation/step-names/${id}`);
}

export function fetchPresentationStepsAnswers(id: PresentationId, isTemplatePreviewPage = true, orgId: OrgId, isPublic = false) {
  return Endpoint.get<Record<string, string>>(`@gc/${isPublic ? 'public/' : ''}presentation/steps-answers/${id}`, {
    params: { isTemplatePreviewPage, orgId },
  });
}
